import * as demo from './demoTemplates';

// Curated WhatsApp Flow templates. Each entry is a valid Flow JSON (version 7.3)
// that can be passed straight into FlowContext.setCurrentFlow({ name, flowJson }).
// The FlowBuilder's useFlowBuilder hook re-hydrates screens/components from this,
// and ensureFootersOnAllScreens fills in any missing Footer components.
//
// Screen ids use the `screen_<letter>` convention so the builder keeps them as-is.

const leadCapture = {
  version: '7.3',
  screens: [
    {
      id: 'screen_a',
      title: 'Get in touch',
      terminal: true,
      layout: {
        type: 'SingleColumnLayout',
        children: [
          { type: 'TextHeading', text: 'Tell us about you' },
          { type: 'TextBody', text: 'We will get back to you within one business day.' },
          { type: 'TextInput', name: 'full_name', label: 'Full name', 'input-type': 'text', required: true },
          { type: 'TextInput', name: 'email', label: 'Email', 'input-type': 'email', required: true },
          // 'text' rather than 'phone', the project's component-structure doc
          // (whatsapp-flow-components-structure.md) only documents text/number/
          // email/password as supported input-types. Using a documented type
          // avoids a Meta validation rejection on publish.
          { type: 'TextInput', name: 'phone', label: 'Phone', 'input-type': 'text', required: false, 'helper-text': 'Digits only' },
          {
            type: 'Dropdown',
            name: 'interest',
            label: 'Interested in',
            required: true,
            'data-source': [
              { id: 'pricing', title: 'Pricing' },
              { id: 'demo', title: 'Product demo' },
              { id: 'support', title: 'Technical support' },
              { id: 'partnership', title: 'Partnership' },
            ],
          },
          {
            type: 'Footer',
            label: 'Submit',
            'on-click-action': {
              name: 'complete',
              payload: {
                full_name: '${form.full_name}',
                email: '${form.email}',
                phone: '${form.phone}',
                interest: '${form.interest}',
              },
            },
          },
        ],
      },
    },
  ],
};

const customerFeedback = {
  version: '7.3',
  screens: [
    {
      id: 'screen_a',
      title: 'Share your feedback',
      terminal: true,
      layout: {
        type: 'SingleColumnLayout',
        children: [
          { type: 'TextHeading', text: 'How was your experience?' },
          { type: 'TextBody', text: 'Your feedback helps us improve.' },
          {
            type: 'RadioButtonsGroup',
            name: 'rating',
            label: 'Overall rating',
            required: true,
            'data-source': [
              { id: '5', title: 'Excellent' },
              { id: '4', title: 'Good' },
              { id: '3', title: 'Okay' },
              { id: '2', title: 'Poor' },
              { id: '1', title: 'Very poor' },
            ],
          },
          {
            type: 'TextArea',
            name: 'comments',
            label: 'What stood out?',
            required: false,
            'helper-text': 'Optional - tell us more',
          },
          {
            type: 'Footer',
            label: 'Send feedback',
            'on-click-action': {
              name: 'complete',
              payload: {
                rating: '${form.rating}',
                comments: '${form.comments}',
              },
            },
          },
        ],
      },
    },
  ],
};

const appointmentBooking = {
  version: '7.3',
  screens: [
    {
      id: 'screen_a',
      title: 'Book an appointment',
      terminal: true,
      layout: {
        type: 'SingleColumnLayout',
        children: [
          { type: 'TextHeading', text: 'Pick a date and service' },
          {
            type: 'Dropdown',
            name: 'service',
            label: 'Service',
            required: true,
            'data-source': [
              { id: 'consultation', title: 'Initial consultation' },
              { id: 'followup', title: 'Follow-up' },
              { id: 'checkup', title: 'Routine checkup' },
            ],
          },
          {
            type: 'DatePicker',
            name: 'appointment_date',
            label: 'Preferred date',
            required: true,
          },
          {
            type: 'RadioButtonsGroup',
            name: 'time_slot',
            label: 'Time slot',
            required: true,
            'data-source': [
              { id: 'morning', title: 'Morning (9am-12pm)' },
              { id: 'afternoon', title: 'Afternoon (12pm-5pm)' },
              { id: 'evening', title: 'Evening (5pm-8pm)' },
            ],
          },
          {
            type: 'TextInput',
            name: 'notes',
            label: 'Notes',
            'input-type': 'text',
            required: false,
          },
          {
            type: 'Footer',
            label: 'Request booking',
            'on-click-action': {
              name: 'complete',
              payload: {
                service: '${form.service}',
                appointment_date: '${form.appointment_date}',
                time_slot: '${form.time_slot}',
                notes: '${form.notes}',
              },
            },
          },
        ],
      },
    },
  ],
};

const newsletterSignup = {
  version: '7.3',
  screens: [
    {
      id: 'screen_a',
      title: 'Join our newsletter',
      terminal: true,
      layout: {
        type: 'SingleColumnLayout',
        children: [
          { type: 'TextHeading', text: 'Stay in the loop' },
          { type: 'TextBody', text: 'Pick the topics you want to hear about.' },
          { type: 'TextInput', name: 'email', label: 'Email', 'input-type': 'email', required: true },
          {
            type: 'CheckboxGroup',
            name: 'interests',
            label: 'Topics',
            required: true,
            'data-source': [
              { id: 'product', title: 'Product updates' },
              { id: 'tips', title: 'Tips and best practices' },
              { id: 'events', title: 'Events and webinars' },
              { id: 'offers', title: 'Special offers' },
            ],
          },
          {
            type: 'OptIn',
            name: 'consent',
            label: 'I agree to receive marketing emails',
            required: true,
          },
          {
            type: 'Footer',
            label: 'Subscribe',
            'on-click-action': {
              name: 'complete',
              payload: {
                email: '${form.email}',
                interests: '${form.interests}',
                consent: '${form.consent}',
              },
            },
          },
        ],
      },
    },
  ],
};

const supportTicket = {
  version: '7.3',
  screens: [
    {
      id: 'screen_a',
      title: 'Open a support ticket',
      terminal: true,
      layout: {
        type: 'SingleColumnLayout',
        children: [
          { type: 'TextHeading', text: 'How can we help?' },
          {
            type: 'Dropdown',
            name: 'category',
            label: 'Category',
            required: true,
            'data-source': [
              { id: 'billing', title: 'Billing' },
              { id: 'technical', title: 'Technical issue' },
              { id: 'account', title: 'Account access' },
              { id: 'other', title: 'Other' },
            ],
          },
          {
            type: 'RadioButtonsGroup',
            name: 'priority',
            label: 'Priority',
            required: true,
            'data-source': [
              { id: 'low', title: 'Low' },
              { id: 'medium', title: 'Medium' },
              { id: 'high', title: 'High' },
              { id: 'urgent', title: 'Urgent' },
            ],
          },
          {
            type: 'TextArea',
            name: 'description',
            label: 'Describe the issue',
            required: true,
            'helper-text': 'Steps to reproduce, error messages, etc.',
          },
          {
            type: 'Footer',
            label: 'Submit ticket',
            'on-click-action': {
              name: 'complete',
              payload: {
                category: '${form.category}',
                priority: '${form.priority}',
                description: '${form.description}',
              },
            },
          },
        ],
      },
    },
  ],
};

// Dynamic (data-exchange) flow: screen A collects the request and asks the
// configured endpoint for live availability via a data_exchange action; the
// endpoint responds with the time slots rendered on screen B. Requires a data
// server to be connected in the flow's Connections.
//
// Screen B's time_slot list is bound to ${data.available_slots}, so the
// data_exchange endpoint is expected to respond with:
//   {
//     "version": "3.0",
//     "screen": "screen_b",
//     "data": {
//       "available_slots": [ { "id": "...", "title": "..." } ]
//     }
//   }
const dynamicAppointmentBooking = {
  version: '7.3',
  data_api_version: '3.0',
  screens: [
    {
      id: 'screen_a',
      title: 'Find availability',
      layout: {
        type: 'SingleColumnLayout',
        children: [
          { type: 'TextHeading', text: 'Tell us what you need' },
          { type: 'TextBody', text: 'We will check live availability for your date.' },
          {
            type: 'Dropdown',
            name: 'service',
            label: 'Service',
            required: true,
            'data-source': [
              { id: 'consultation', title: 'Initial consultation' },
              { id: 'followup', title: 'Follow-up' },
              { id: 'checkup', title: 'Routine checkup' },
            ],
          },
          {
            type: 'DatePicker',
            name: 'preferred_date',
            label: 'Preferred date',
            required: true,
          },
          {
            type: 'Footer',
            label: 'Check times',
            'on-click-action': {
              name: 'data_exchange',
              payload: {
                service: '${form.service}',
                preferred_date: '${form.preferred_date}',
              },
            },
          },
        ],
      },
    },
    {
      id: 'screen_b',
      title: 'Choose a time',
      terminal: true,
      layout: {
        type: 'SingleColumnLayout',
        children: [
          { type: 'TextHeading', text: 'Available time slots' },
          {
            type: 'RadioButtonsGroup',
            name: 'time_slot',
            label: 'Time slot',
            required: true,
            'data-source': '${data.available_slots}',
          },
          {
            type: 'Footer',
            label: 'Confirm booking',
            'on-click-action': {
              name: 'complete',
              payload: {
                time_slot: '${form.time_slot}',
              },
            },
          },
        ],
      },
    },
  ],
};

export const flowTemplates = [
  {
    id: 'lead-capture',
    name: 'Lead capture',
    description: 'Collect name, email, phone, and what the lead is interested in.',
    category: 'Sales',
    flowJson: leadCapture,
  },
  {
    id: 'customer-feedback',
    name: 'Customer feedback',
    description: 'Rate the experience and leave optional comments.',
    category: 'Feedback',
    flowJson: customerFeedback,
  },
  {
    id: 'appointment-booking',
    name: 'Appointment booking',
    description: 'Pick a service, date, and time slot to request a booking.',
    category: 'Scheduling',
    flowJson: appointmentBooking,
  },
  {
    id: 'newsletter-signup',
    name: 'Newsletter signup',
    description: 'Email plus topic preferences with explicit consent.',
    category: 'Marketing',
    flowJson: newsletterSignup,
  },
  {
    id: 'support-ticket',
    name: 'Support ticket',
    description: 'Triage incoming support requests by category and priority.',
    category: 'Support',
    flowJson: supportTicket,
  },
  {
    id: 'dynamic-appointment-booking',
    name: 'Dynamic appointment booking',
    description:
      'Asks your data endpoint for live availability, then shows real time slots. Requires a data endpoint (Save → Advanced).',
    category: 'Scheduling',
    flowJson: dynamicAppointmentBooking,
  },
  // The flows behind the landing demo's "Make this flow yours", hidden from the gallery and
  // reachable only through that deep link (?template=<id>). See demoTemplates.js.
  {
    id: 'demo-dental-booking',
    name: 'Book an appointment',
    description: 'The Bright Smile Dental booking from the FlowGate demo: service, referral letter, practitioner and time, then a review.',
    category: 'Scheduling',
    hidden: true,
    flowJson: demo.clinic,
  },
  {
    id: 'demo-cafe-feedback',
    name: 'How was your visit?',
    description: 'The Brew & Co. feedback from the FlowGate demo: a rating, then optional comments.',
    category: 'Feedback',
    hidden: true,
    flowJson: demo.cafe,
  },
  {
    id: 'demo-kitchen-quote',
    name: 'Get a tailored quote',
    description: 'The Nordic Kitchens quote from the FlowGate demo: project and estimate, contact details, then a review.',
    category: 'Sales',
    hidden: true,
    flowJson: demo.kitchen,
  },
  {
    id: 'demo-viewing-booking',
    name: 'Book a viewing',
    description: 'The Parkside Lettings viewing from the FlowGate demo: day and time, applicant details, then a review.',
    category: 'Scheduling',
    hidden: true,
    flowJson: demo.viewing,
  },
  {
    id: 'demo-garage-booking',
    name: 'Book your car in',
    description: 'The Northway Motors booking from the FlowGate demo: job and drop-off, the car and a photo, then a review.',
    category: 'Scheduling',
    hidden: true,
    flowJson: demo.garage,
  },
  {
    id: 'demo-home-quote',
    name: 'Request a visit',
    description: 'The Brightwater Heating callout from the FlowGate demo: the problem, photos and address, then a review.',
    category: 'Sales',
    hidden: true,
    flowJson: demo.homeQuote,
  },
];

export const getTemplate = (id) => flowTemplates.find((t) => t.id === id);
